import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { AlertTriangle, Play, Plus, Save, Trash2 } from 'lucide-react'
import { EmptyState, FormDialog, PageHeader } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useDatasets, useReportMutations } from '../api'
import { OPERATOR_LABEL } from '../types'
import type { FilterSpec, ReportRunResult } from '../types'

/**
 * A cell as text.
 *
 * <p>The rows come back as `unknown[]` because a report's shape is chosen at run time. Dates
 * and numbers arrive as strings from the JSON, and anything the server sent as an object gets
 * its JSON rather than "[object Object]" — which is the one rendering that tells the reader
 * nothing at all.
 */
function cellText(cell: unknown): string {
  if (cell === null || cell === undefined) return '—'
  if (typeof cell === 'string') return cell
  if (typeof cell === 'number' || typeof cell === 'boolean') return cell.toString()
  return JSON.stringify(cell) ?? ''
}

/**
 * The builder.
 *
 * <p>Columns, filters and a group-by over one dataset. There is no SQL box, and the statement
 * that comes back is shown rather than hidden — a report somebody disputes is unarguable with
 * the query in front of them.
 */
export function ReportBuilderPage() {
  useBreadcrumb()
  const [params] = useSearchParams()
  const datasets = useDatasets()
  const actions = useReportMutations()

  const [datasetKey, setDatasetKey] = useState(params.get('dataset') ?? '')
  const [columns, setColumns] = useState<string[]>([])
  const [filters, setFilters] = useState<FilterSpec[]>([])
  const [groupBy, setGroupBy] = useState<string[]>([])
  const [result, setResult] = useState<ReportRunResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [draft, setDraft] = useState({ name: '', description: '', sharing: 'PRIVATE', format: 'XLSX' })

  const dataset = useMemo(
    () => datasets.data?.find((entry) => entry.key === datasetKey)
      ?? datasets.data?.[0],
    [datasets.data, datasetKey])

  function choose(key: string) {
    setDatasetKey(key)
    setColumns([])
    setFilters([])
    setGroupBy([])
    setResult(null)
    setError(null)
  }

  const definition = {
    columns,
    filters,
    groupBy,
    orderBy: null,
    orderDescending: false,
    limit: null,
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Report builder"
        subtitle="Columns, filters and grouping over one dataset. No SQL, by design."
        actions={
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={!dataset || actions.run.isPending}
              onClick={() => {
                if (!dataset) return
                setError(null)
                actions.run.mutate({ datasetKey: dataset.key, definition }, {
                  onSuccess: setResult,
                  onError: (caught) => {
                    setResult(null)
                    setError(toApiProblem(caught)?.detail ?? 'That report could not be run')
                  },
                })
              }}
            >
              <Play className="size-4" aria-hidden="true" />
              Run it
            </Button>
            <Button
              type="button"
              disabled={!dataset || columns.length === 0}
              onClick={() => { setError(null); setSaving(true) }}
            >
              <Save className="size-4" aria-hidden="true" />
              Save
            </Button>
          </div>
        }
      />

      {datasets.isPending && <Skeleton className="h-32" />}

      {error && (
        <p role="alert" className="rounded-md border border-[var(--color-danger)] bg-[var(--color-tint-red)] p-3 text-sm text-[var(--color-danger)]">
          {error}
        </p>
      )}

      {dataset && (
        <div className="grid gap-4 lg:grid-cols-3">
          <Card className="p-4 lg:col-span-1">
            <div className="space-y-3">
              <div>
                <Label htmlFor="builder-dataset">Report on</Label>
                <Select
                  id="builder-dataset"
                  className="w-full"
                  value={dataset.key}
                  onChange={(event) => choose(event.target.value)}
                >
                  {datasets.data?.map((entry) => (
                    <option key={entry.key} value={entry.key}>{entry.label}</option>
                  ))}
                </Select>
                {dataset.personalDataNote && (
                  <p className="mt-2 flex items-start gap-1 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                    <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                    {dataset.personalDataNote}
                  </p>
                )}
              </div>

              <fieldset>
                <legend className="text-sm font-medium text-[var(--color-heading)]">Columns</legend>
                <p className="mb-2 text-xs text-[var(--color-muted)]">
                  These are the only columns this dataset offers. Identifiers, bank details and
                  dates of birth are not among them, and cannot be asked for.
                </p>
                <div className="space-y-1">
                  {dataset.columns.map((column) => (
                    <label key={column.key} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="size-4"
                        checked={columns.includes(column.key)}
                        onChange={(event) => setColumns(event.target.checked
                          ? [...columns, column.key]
                          : columns.filter((key) => key !== column.key))}
                      />
                      {column.label}
                    </label>
                  ))}
                </div>
              </fieldset>

              <fieldset>
                <legend className="text-sm font-medium text-[var(--color-heading)]">Group by</legend>
                <div className="space-y-1">
                  {dataset.columns.filter((column) => column.groupable).map((column) => (
                    <label key={column.key} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="size-4"
                        checked={groupBy.includes(column.key)}
                        onChange={(event) => setGroupBy(event.target.checked
                          ? [...groupBy, column.key]
                          : groupBy.filter((key) => key !== column.key))}
                      />
                      {column.label}
                    </label>
                  ))}
                </div>
                {groupBy.length > 0 && (
                  <p className="mt-1 text-xs text-[var(--color-muted)]">
                    Anything not grouped becomes a count. Nothing is summed or averaged: a
                    builder that guessed which one you meant would produce numbers nobody can
                    defend.
                  </p>
                )}
              </fieldset>
            </div>
          </Card>

          <Card className="p-4 lg:col-span-2">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[var(--color-heading)]">Filters</h3>
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => setFilters([...filters, {
                  column: dataset.columns[0]?.key ?? '', operator: 'EQ', value: '',
                }])}
              >
                <Plus className="size-4" aria-hidden="true" />
                Add a filter
              </Button>
            </div>

            {filters.length === 0 && (
              <p className="text-sm text-[var(--color-muted)]">
                No filter — every row this dataset can see, up to the cap.
              </p>
            )}

            <div className="space-y-2">
              {filters.map((filter, index) => (
                <div key={index} className="flex flex-wrap items-center gap-2">
                  <Select
                    aria-label="Column"
                    value={filter.column}
                    onChange={(event) => setFilters(filters.map((row, at) =>
                      at === index ? { ...row, column: event.target.value } : row))}
                  >
                    {dataset.columns.map((column) => (
                      <option key={column.key} value={column.key}>{column.label}</option>
                    ))}
                  </Select>
                  <Select
                    aria-label="Operator"
                    value={filter.operator}
                    onChange={(event) => setFilters(filters.map((row, at) =>
                      at === index ? { ...row, operator: event.target.value } : row))}
                  >
                    {Object.entries(OPERATOR_LABEL).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Select>
                  {!filter.operator.startsWith('IS_') && (
                    <Input
                      aria-label="Value"
                      className="w-48"
                      value={filter.value ?? ''}
                      onChange={(event) => setFilters(filters.map((row, at) =>
                        at === index ? { ...row, value: event.target.value } : row))}
                    />
                  )}
                  <Button
                    type="button" variant="outline" size="sm"
                    aria-label="Remove filter"
                    onClick={() => setFilters(filters.filter((_, at) => at !== index))}
                  >
                    <Trash2 className="size-4" aria-hidden="true" />
                  </Button>
                </div>
              ))}
            </div>

            {result && (
              <div className="mt-4 space-y-2">
                <p className="text-sm text-[var(--color-body)]">
                  {result.rowCount} row(s).
                  {result.truncationNote && (
                    <span className="ms-1 text-[var(--color-muted)]">{result.truncationNote}</span>
                  )}
                </p>
                <div className="max-h-80 overflow-auto rounded-md border border-[var(--color-border)]">
                  <table className="w-full text-xs">
                    <thead className="bg-[var(--color-page)] text-[var(--color-muted)]">
                      <tr>
                        {result.columns.map((column) => (
                          <th key={column} className="p-2 text-start font-medium">{column}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.rows.slice(0, 100).map((row, index) => (
                        <tr key={index} className="border-t border-[var(--color-border)]">
                          {row.map((cell, at) => (
                            <td key={at} className="p-2">{cellText(cell)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <details>
                  <summary className="cursor-pointer text-xs text-[var(--color-muted)]">
                    What this asked
                  </summary>
                  <pre className="mt-1 overflow-x-auto rounded-md bg-[var(--color-page)] p-2 text-[11px]">
                    {result.resolvedSql}
                  </pre>
                </details>
              </div>
            )}
            {!result && !actions.run.isPending && (
              <div className="mt-4">
                <EmptyState
                  title="Nothing run yet"
                  description="Choose columns and press Run. The statement it produces is shown with the result."
                />
              </div>
            )}
          </Card>
        </div>
      )}

      <FormDialog
        open={saving}
        onOpenChange={(open) => { if (!open) { setSaving(false); setError(null) } }}
        title="Save this report"
        submitLabel="Save it"
        loading={actions.save.isPending}
        error={error}
        onSubmit={() => {
          if (!dataset) return
          void (async () => {
            setError(null)
            try {
              await actions.save.mutateAsync({
                name: draft.name,
                description: draft.description || null,
                sourceKind: 'DATASET',
                sourceKey: dataset.key,
                definition,
                defaultFormat: draft.format,
                sharing: draft.sharing,
                sharedRoleIds: [],
                active: true,
              })
              setSaving(false)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That report could not be saved')
            }
          })()
        }}
      >
        <div className="space-y-3">
          <div>
            <Label htmlFor="save-name">Name</Label>
            <Input
              id="save-name" value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="save-description">What it is for</Label>
            <Textarea
              id="save-description" value={draft.description}
              onChange={(event) => setDraft({ ...draft, description: event.target.value })}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="save-sharing">Who can see it</Label>
              <Select
                id="save-sharing" className="w-full" value={draft.sharing}
                onChange={(event) => setDraft({ ...draft, sharing: event.target.value })}
              >
                <option value="PRIVATE">Just me</option>
                <option value="EVERYONE">Everyone who can read reports</option>
              </Select>
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                Private by default: a report you built for yourself is not one the whole company
                should find in a catalogue.
              </p>
            </div>
            <div>
              <Label htmlFor="save-format">Download as</Label>
              <Select
                id="save-format" className="w-full" value={draft.format}
                onChange={(event) => setDraft({ ...draft, format: event.target.value })}
              >
                <option value="XLSX">Spreadsheet</option>
                <option value="CSV">CSV</option>
                <option value="PDF">PDF</option>
              </Select>
            </div>
          </div>
        </div>
      </FormDialog>
    </div>
  )
}
